"use client";

import React, { useCallback } from "react";
import {
  Bars3Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { RootState } from "../../app/rootReducer";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";
import clsx from "clsx";
import { useNavigate, Link } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import ThemeToggle from "@/components/ThemeToggle";
import { stopCrossAppImpersonation } from "@/api/impersonation/impersonationApi";
import { useIsImpersonating } from "@/hooks/useIsImpersonating";

interface TopNavBarProps {
  onSidebarToggle: () => void;
}

const TopNavBar: React.FC<TopNavBarProps> = ({ onSidebarToggle }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { isMobile, toggleSidebar } = useSidebar();
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );
  const cartTotalCount = useSelector(
    (state: RootState) => state.productStoreCart.totalCount,
  );

  // Cart is read straight from the slice (which hydrates from
  // localStorage at module load). No API ping on mount anymore.
  const fullName = userAppSettings
    ? `${userAppSettings.firstName} ${userAppSettings.lastName}`
    : "Your Name";
  const initials = fullName
    .split(" ")
    .map((n) => n[0])
    .join("");
  const isImpersonating = useIsImpersonating();

  const handleExitImpersonation = useCallback(async () => {
    await stopCrossAppImpersonation();
    await getAccessTokenSilently({ cacheMode: "off" });
    localStorage.removeItem("selectedCompanyUuid");
    // Hard navigation to root re-bootstraps userAppSettings/permissions for the
    // real (no-longer-impersonated) user; a soft navigate would leave them stale.
    window.location.href = "/";
  }, [getAccessTokenSilently]);

  const handleHamburgerClick = () => {
    if (isMobile) {
      toggleSidebar();
    } else {
      onSidebarToggle();
    }
  };

  return (
    <div className="sticky top-0 z-10 flex h-14 shrink-0 items-center border-b px-4 sm:gap-x-6 sm:px-6 lg:px-8 bg-white dark:bg-slate-900 border-gray-200 dark:border-slate-800/50">
      <button
        className="-m-2.5 p-2.5 transition-colors text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
        onClick={handleHamburgerClick}
        type="button"
      >
        <span className="sr-only">Open menu</span>
        <Bars3Icon aria-hidden="true" className="h-6 w-6" />
      </button>
      {/* Centered, input-style search trigger — wide and prominent so search
          is discoverable from anywhere in the app. */}
      <div className="flex flex-1 justify-center px-3 sm:px-6">
        <button
          className="flex w-full max-w-xl items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-left transition-colors hover:bg-gray-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
          onClick={() => dispatch(openSearchModal())}
          title="Search (⌘K)"
          type="button"
        >
          <MagnifyingGlassIcon
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-gray-500 dark:text-slate-400"
          />
          <span className="flex-1 truncate text-sm text-gray-500 dark:text-slate-400">
            Search…
          </span>
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded border text-gray-500 bg-white border-gray-200 dark:text-slate-500 dark:bg-slate-900 dark:border-slate-700">
            {navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}K
          </kbd>
        </button>
      </div>
      <div className="flex items-center gap-x-4 lg:gap-x-6">
        {/* Cart + Theme Toggle */}
        <div className="flex items-center gap-3">
          <Link
            className="relative flex items-center justify-center p-1.5 rounded-md transition-colors text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white"
            title="Cart"
            to="/product-directory/checkout"
          >
            <ShoppingCartIcon aria-hidden="true" className="h-6 w-6" />
            {cartTotalCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[1rem] px-1 text-[10px] font-medium leading-none text-white bg-red-600 rounded-full">
                {cartTotalCount}
              </span>
            )}
          </Link>
          <ThemeToggle />
        </div>

        <Menu as="div" className="relative">
          <MenuButton className="-m-1.5 flex items-center p-1.5">
            <span className="sr-only">Open user menu</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-600">
              <span className="text-sm font-medium leading-none text-white">
                {initials}
              </span>
            </span>
            <span className="hidden lg:flex lg:items-center">
              <span
                aria-hidden="true"
                className="ml-4 text-sm font-semibold leading-6 text-gray-900 dark:text-slate-400"
              >
                {fullName}
              </span>
              <ChevronDownIcon
                aria-hidden="true"
                className="ml-2 h-5 w-5 text-gray-400 dark:text-slate-500"
              />
            </span>
          </MenuButton>
          <MenuItems className="absolute right-0 z-50 mt-2.5 w-32 origin-top-right rounded-md bg-white dark:bg-slate-800 py-2 shadow-lg ring-1 ring-gray-200 dark:ring-slate-700 transition focus:outline-none">
            <MenuItem>
              {({ active }) => (
                <Link
                  className={clsx(
                    active ? "bg-gray-100 dark:bg-slate-700" : "",
                    "block px-3 py-1 text-sm leading-6 text-gray-900 dark:text-slate-100",
                  )}
                  to="/hub/settings"
                >
                  Settings
                </Link>
              )}
            </MenuItem>
            <MenuItem>
              {({ active }) => (
                <button
                  className={clsx(
                    active ? "bg-gray-100 dark:bg-slate-700" : "",
                    "block w-full text-left px-3 py-1 text-sm leading-6 text-gray-900 dark:text-slate-100",
                  )}
                  onClick={() => navigate("/logout")}
                >
                  Sign out
                </button>
              )}
            </MenuItem>
            {isImpersonating && (
              <>
                <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={clsx(
                        active ? "bg-gray-100 dark:bg-slate-700" : "",
                        "block w-full text-left px-3 py-1 text-sm leading-6 text-red-600 dark:text-red-400",
                      )}
                      onClick={handleExitImpersonation}
                    >
                      Exit Impersonation
                    </button>
                  )}
                </MenuItem>
              </>
            )}
          </MenuItems>
        </Menu>
      </div>
    </div>
  );
};

export default TopNavBar;
